import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { header } from './header.encry';
import '../css/promotions-modal.css';
import PromotionsGlovoOverview from './PromotionsGlovoOverview';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
const DATA_PIC_PATH = process.env.REACT_APP_DATA_PIC_PATH;

const PromotionsModal = ({
  open,
  onClose,
  glovoPlatform,
  glovoPlateformIds = [],
  allProducts = [],
  allDiscounts = [],
  productCategories = [],
}) => {
  const [activeTab, setActiveTab] = useState('glovo');
  const [glovoStores, setGlovoStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(20);
  const [productSearch, setProductSearch] = useState('');
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const [discountFilter, setDiscountFilter] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [showStoreFilters, setShowStoreFilters] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const normalizeId = useCallback((value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      return String(value.$oid || value._id || value.id || value.value || '');
    }
    return String(value);
  }, []);

  const normalizeStringArray = useCallback((value) => {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) {
      return value
        .map((v) => (v === null || v === undefined ? '' : String(v)))
        .filter(Boolean);
    }
    return [String(value)];
  }, []);

  const parseNumber = useCallback((value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  }, []);

  const resolveProductImage = useCallback((product) => {
    const candidate = product?.url_product || product?.image_url || product?.picture;
    if (!candidate) return 'https://via.placeholder.com/300x200?text=No+Image';
    const value = String(candidate);
    if (/^https?:\/\//i.test(value)) return value;
    const base = (DATA_PIC_PATH || '').replace(/\/$/, '');
    const path = value.replace(/^\//, '');
    return base ? `${base}/${path}` : value;
  }, []);

  const fetchGlovoStores = useCallback(async () => {
    try {
      if (!glovoPlatform || !glovoPlatform.store_ids || glovoPlatform.store_ids.length === 0) {
        console.log('No platform or store_ids found', glovoPlatform);
        setGlovoStores([]);
        return;
      }

      // Fetch all stores
      const storesResponse = await axios.get(`${API_URL}/stores`, header());
      const allStores = storesResponse.data.response || storesResponse.data || [];

      console.log('Platform store_ids:', glovoPlatform.store_ids);
      console.log('All stores:', allStores.map(s => ({ id: s._id, name: s.storeName })));

      // Filter stores that are in platform.store_ids
      const filteredStores = allStores.filter(store => {
        const storeId = store._id?.$oid || store._id;
        const isMatch = glovoPlatform.store_ids.some(platformStoreId => {
          const platformId = platformStoreId?.$oid || platformStoreId;
          return String(platformId) === String(storeId);
        });
        return isMatch;
      });

      console.log('Filtered Glovo stores:', filteredStores.map(s => ({ id: s._id, name: s.storeName })));
      setGlovoStores(filteredStores);
    } catch (error) {
      console.error('Error fetching Glovo stores:', error);
      setGlovoStores([]);
    }
  }, [glovoPlatform]);

  useEffect(() => {
    if (open) {
      setHasSearched(false);
      setCurrentPage(1);
      if (glovoPlatform) {
        fetchGlovoStores();
      } else {
        setGlovoStores([]);
      }
    }
  }, [open, glovoPlatform, fetchGlovoStores]);

  const getAllDiscountProducts = () => {
    const productMatches = [];
    const today = new Date().toISOString().split('T')[0];
    
    // Filter discounts by status
    let filteredDiscounts = allDiscounts.filter(discount => {
      // Status filter
      if (statusFilter && discount.status !== statusFilter) return false;
      
      // Date filter for active discounts
      if (statusFilter === 'active') {
        if (discount.startDate && discount.startDate > today) return false;
        if (discount.endDate && discount.endDate < today) return false;
      }
      
      // Store filter (selected stores are Glovo store codes like discount.store_id)
      if (selectedStores.length > 0) {
        const selectedStoreCodes = selectedStores.map((s) => String(s.value));
        const discountStoreId = discount?.store_id ? String(discount.store_id) : '';
        const vendorCodes = Array.isArray(discount?.vendorCodes) ? discount.vendorCodes.map(String) : [];
        const storeMatch =
          (discountStoreId && selectedStoreCodes.includes(discountStoreId)) ||
          vendorCodes.some((code) => selectedStoreCodes.includes(code));

        if (!storeMatch) return false;
      }
      
      return true;
    });

    // For each discount, find matching products
    const glovoIdSet = new Set((Array.isArray(glovoPlateformIds) ? glovoPlateformIds : []).map((id) => String(id)));

    filteredDiscounts.forEach((discount) => {
      const discountProductIds = (discount.productMenuSelectionBehavior?.products || [])
        .map((p) => (p?.id === null || p?.id === undefined ? '' : String(p.id)))
        .filter(Boolean);
      if (discountProductIds.length === 0) return;

      allProducts.forEach((product) => {
        const plateformEntries = Array.isArray(product?.plateforms) ? product.plateforms : [];
        const filteredByIds =
          glovoIdSet.size > 0
            ? plateformEntries.filter((entry) => glovoIdSet.has(normalizeId(entry?.plateform_id)))
            : plateformEntries;

        const candidateEntries = filteredByIds.length > 0 ? filteredByIds : plateformEntries;

        candidateEntries.forEach((entry) => {
          const entryRemoteIds = normalizeStringArray(entry?.remote_id || entry?.remoteId || entry?.id);
          const remoteIds = Array.from(new Set(entryRemoteIds));
          if (remoteIds.length === 0) return;

          const matchedRemoteId = discountProductIds.find((discountId) =>
            remoteIds.some((remoteId) => String(remoteId) === String(discountId))
          );

          if (!matchedRemoteId) return;

          // Get price from roadmap if available, otherwise use regular_price
          let regularPrice = parseNumber(product?.regular_price ?? product?.regularPrice ?? product?.price, 0);
          
          // Try to get price from roadmap
          const roadmapData = (Array.isArray(product?.plateforms) ? product.plateforms : [])
            .flatMap(p => Array.isArray(p.roadmap) ? p.roadmap : []);
          
          if (roadmapData.length > 0) {
            // Use the first roadmap entry's unitPrice, or aggregate if multiple stores
            const roadmapPrice = roadmapData[0]?.data?.unitPrice;
            if (roadmapPrice && roadmapPrice > 0) {
              regularPrice = parseNumber(roadmapPrice, regularPrice);
            }
          }

          const discountPercentage = parseNumber(discount?.discountPercentageRewardBehavior?.percentage, 0);
          const salePrice = regularPrice - (regularPrice * discountPercentage) / 100;

          productMatches.push({
            ...product,
            discount,
            regularPrice,
            salePrice,
            discountPercentage,
            remoteId: matchedRemoteId,
          });
        });
      });
    });

    return productMatches;
  };

  const filterProducts = (productList) => {
    return productList.filter(product => {
      // Name filter
      const productName = product.name || product.productName || '';
      if (productSearch && !productName.toLowerCase().includes(productSearch.toLowerCase())) {
        return false;
      }

      // Price filter (use regularPrice from the enriched product object, which now includes roadmap price)
      const price = product.regularPrice || parseFloat(product.regular_price || product.price || 0);
      if (priceFilter.min && price < parseFloat(priceFilter.min)) return false;
      if (priceFilter.max && price > parseFloat(priceFilter.max)) return false;

      // Discount filter (use discountPercentage from the enriched product object)
      if (discountFilter) {
        const discountPercent = product.discountPercentage || 0;
        const minDiscount = parseFloat(discountFilter);
        if (discountPercent < minDiscount) return false;
      }

      return true;
    });
  };

  const getStoreNames = (vendorCodes) => {
    if (!Array.isArray(vendorCodes)) return [];
    return vendorCodes
      .map(code => {
        const store = glovoStores.find(s => String(s.storeCode) === String(code) || String(s.store_id) === String(code));
        return store?.storeName || store?.name || code;
      })
      .filter(Boolean);
  };

  const allDiscountProducts = getAllDiscountProducts();
  const filteredProducts = filterProducts(allDiscountProducts);
  
  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  if (!open) return null;

  return (
    <>
      <style>{`
        #promotionsModal {
          z-index: 9999 !important;
        }
        #promotionsModal ~ .modal-backdrop {
          z-index: 9998 !important;
        }
        #promotionsModal .modal-dialog {
          max-width: 96vw !important;
          min-width: 96vw !important;
          width: 96vw !important;
          margin: 2vh auto !important;
        }
        #promotionsModal .modal-content {
          max-width: 100% !important;
          min-width: 100% !important;
          width: 100% !important;
          height: 96vh !important;
        }
        #promotionsModal .modal-body {
          width: 100% !important;
        }
        .promotion-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          transition: all 0.3s ease;
        }
        .promotion-card {
          transition: all 0.3s ease;
        }
      `}</style>
      <div className="modal fade show" id="promotionsModal" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '1rem 1.5rem' }}>
              <h5 className="modal-title d-flex align-items-center mb-0">
                <i className="fas fa-tags me-3" style={{ fontSize: '1.5rem' }}></i>
                <span className="fw-bold" style={{ fontSize: '1.5rem' }}>Gestion des Promotions</span>
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
                style={{ fontSize: '1.2rem' }}
              ></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', padding: '1rem 1.5rem' }}>
              {/* Tabs */}
              <ul className="nav nav-pills mb-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '8px' }}>
                <li className="nav-item" style={{ flex: 1 }}>
                  <button
                    className={`nav-link w-100 ${activeTab === 'glovo' ? 'active' : ''}`}
                    onClick={() => setActiveTab('glovo')}
                    style={{
                      borderRadius: '8px',
                      backgroundColor: activeTab === 'glovo' ? '#667eea' : 'transparent',
                      color: activeTab === 'glovo' ? 'white' : '#6c757d',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="fas fa-shipping-fast me-2"></i>
                    Promotions Glovo
                  </button>
                </li>
                <li className="nav-item ms-2" style={{ flex: 1 }}>
                  <button
                    className={`nav-link w-100 ${activeTab === 'glovo-overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('glovo-overview')}
                    style={{
                      borderRadius: '8px',
                      backgroundColor: activeTab === 'glovo-overview' ? '#667eea' : 'transparent',
                      color: activeTab === 'glovo-overview' ? 'white' : '#6c757d',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="fas fa-chart-line me-2"></i>
                    Promotions Glovo Overview
                  </button>
                </li>
              </ul>

              {/* Glovo Tab Content */}
              {activeTab === 'glovo' && (
                <>
                  {/* Step 1: Store and Status Filters */}
                  <div className="mb-4 p-3 rounded shadow-sm" style={{ backgroundColor: '#ffffff', border: '1px solid #e9ecef' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ cursor: 'pointer' }} onClick={() => setShowStoreFilters(!showStoreFilters)}>
                      <h6 className="mb-0 fw-bold" style={{ color: '#495057' }}>
                        <i className="fas fa-filter me-2" style={{ color: '#667eea' }}></i>
                        Filtrer les promotions
                      </h6>
                      <i className={`fas fa-chevron-${showStoreFilters ? 'up' : 'down'}`} style={{ color: '#667eea' }}></i>
                    </div>
                    {showStoreFilters && (
                    <div className="row g-3 mt-2">
                      <div className="col-md-5">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-store me-1"></i>
                          Magasins Glovo
                        </label>
                        <Select
                          isMulti
                          options={glovoStores.map(store => {
                            const storeCode = store.store_id || store.storeCode;
                            return {
                              value: storeCode ? String(storeCode) : normalizeId(store._id),
                              label: `${store.storeName || store.name || 'Store'}${storeCode ? ` (${storeCode})` : ''}`
                            };
                          })}
                          value={selectedStores}
                          onChange={(selected) => setSelectedStores(selected || [])}
                          placeholder="Sélectionner des magasins..."
                          noOptionsMessage={() => "Aucun magasin disponible"}
                          isSearchable
                          className="basic-multi-select"
                          classNamePrefix="select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: '8px',
                              borderColor: '#ced4da',
                              minHeight: '42px'
                            })
                          }}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-toggle-on me-1"></i>
                          Statut
                        </label>
                        <select
                          className="form-select"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          style={{ borderRadius: '8px', height: '42px' }}
                        >
                          <option value="">Tous</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="col-md-4 d-flex align-items-end">
                        <button
                          className="btn btn-primary w-100"
                          onClick={() => {
                            setHasSearched(true);
                            setCurrentPage(1);
                            setShowStoreFilters(false);
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            height: '42px',
                            fontWeight: '600',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                          }}
                        >
                          <i className="fas fa-search me-2"></i>
                          Rechercher les promotions
                        </button>
                      </div>
                    </div>
                    )}
                  </div>

                  {/* Step 2: Display Results */}
                  {!hasSearched ? (
                    <div className="text-center py-5" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                      <i className="fas fa-search fa-4x mb-4" style={{ color: '#667eea' }}></i>
                      <h5 className="text-muted">Commencez votre recherche</h5>
                      <p className="text-muted">Sélectionnez des magasins (optionnel) et cliquez sur Rechercher</p>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-5" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                      <i className="fas fa-info-circle fa-4x mb-4" style={{ color: '#ffc107' }}></i>
                      <h5 className="text-muted">Aucune promotion trouvée</h5>
                      <p className="text-muted">Essayez de modifier vos critères de recherche</p>
                    </div>
                  ) : (
                    <>
                      {/* Advanced Filters Button */}
                      <div className="mb-3">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => setShowAdvancedFilters(true)}
                          style={{ borderRadius: '8px', fontWeight: '600' }}
                        >
                          <i className="fas fa-sliders-h me-2"></i>
                          Filtres avancés
                          {(productSearch || priceFilter.min || priceFilter.max || discountFilter) && (
                            <span className="badge bg-danger ms-2">Active</span>
                          )}
                        </button>
                      </div>

                      {/* Products Grid Header */}
                      <div className="mb-3 d-flex justify-content-between align-items-center p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <h6 className="mb-0 fw-bold" style={{ color: '#495057' }}>
                          <i className="fas fa-gift me-2" style={{ color: '#667eea' }}></i>
                          Produits en promotion
                          <span className="badge bg-primary ms-2" style={{ fontSize: '0.9rem' }}>{filteredProducts.length}</span>
                        </h6>
                        <div className="text-muted small">
                          Page {currentPage} sur {totalPages}
                        </div>
                      </div>

                      {/* Products Grid (Scrollable) */}
                      <div style={{ maxHeight: 'calc(100vh - 450px)', minHeight: '500px', overflowY: 'auto', paddingRight: '8px' }}>
                        {currentProducts.length === 0 ? (
                          <div className="text-center py-5" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
                            <i className="fas fa-box-open fa-3x mb-3" style={{ color: '#6c757d' }}></i>
                            <p className="text-muted">Aucun produit correspondant à vos critères</p>
                          </div>
                        ) : (
                          <div className="row g-3">
                            {currentProducts.map((item, idx) => (
                              <div key={`${item._id}-${item.discount._id}-${idx}`} className="col-xl-2 col-lg-3 col-md-4 col-12">
                                <div className="card h-100 promotion-card" style={{ 
                                  border: 'none', 
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  borderRadius: '12px',
                                  overflow: 'hidden'
                                }}>
                                  <div className="position-relative">
                                    <img
                                      src={resolveProductImage(item)}
                                      alt={item.name || item.productName}
                                      style={{ 
                                        width: '100%', 
                                        height: '180px', 
                                        objectFit: 'cover',
                                        background: '#f8f9fa'
                                      }}
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                                      }}
                                    />
                                    <span 
                                      className="position-absolute badge" 
                                      style={{ 
                                        top: '10px', 
                                        right: '10px',
                                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                        fontSize: '1rem',
                                        padding: '8px 12px',
                                        borderRadius: '20px',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(245, 87, 108, 0.4)'
                                      }}
                                    >
                                      -{item.discountPercentage}%
                                    </span>
                                  </div>
                                  <div className="card-body p-3">
                                    <h6 className="card-title mb-2" style={{
                                      fontSize: '0.95rem',
                                      fontWeight: '600',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      minHeight: '38px',
                                      color: '#2d3748'
                                    }}>
                                      {item.name || item.productName}
                                    </h6>
                                    <div className="mb-2">
                                      <div className="text-decoration-line-through text-muted small">
                                        {Number(item.regularPrice || 0).toFixed(2)} MAD
                                      </div>
                                      <div className="fw-bold" style={{ fontSize: '1.25rem', color: '#667eea' }}>
                                        {Number(item.salePrice || 0).toFixed(2)} MAD
                                      </div>
                                      <div className="text-success small fw-semibold">
                                        <i className="fas fa-piggy-bank me-1"></i>
                                        Économie: {(Number(item.regularPrice || 0) - Number(item.salePrice || 0)).toFixed(2)} MAD
                                      </div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1">
                                      {getStoreNames(item.discount.vendorCodes).map((storeName, storeIdx) => (
                                        <span 
                                          key={storeIdx} 
                                          className="badge" 
                                          style={{ 
                                            backgroundColor: '#e7f3ff',
                                            color: '#0066cc',
                                            fontSize: '0.75rem',
                                            padding: '4px 8px',
                                            fontWeight: '600'
                                          }}
                                        >
                                          <i className="fas fa-store me-1"></i>
                                          {storeName}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <nav className="mt-4">
                          <ul className="pagination justify-content-center">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{ borderRadius: '8px 0 0 8px', fontWeight: '600' }}
                              >
                                <i className="fas fa-chevron-left me-1"></i>
                                Précédent
                              </button>
                            </li>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              const page = i + 1;
                              return (
                                <li
                                  key={page}
                                  className={`page-item ${currentPage === page ? 'active' : ''}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                    style={{
                                      fontWeight: '600',
                                      backgroundColor: currentPage === page ? '#667eea' : 'transparent',
                                      borderColor: currentPage === page ? '#667eea' : '#dee2e6'
                                    }}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            })}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{ borderRadius: '0 8px 8px 0', fontWeight: '600' }}
                              >
                                Suivant
                                <i className="fas fa-chevron-right ms-1"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Glovo Overview Tab Content */}
              {activeTab === 'glovo-overview' && (
                <div className="p-3">
                  <PromotionsGlovoOverview
                    glovoStores={glovoStores}
                    allDiscounts={allDiscounts}
                    allProducts={allProducts}
                    glovoPlateformIds={glovoPlateformIds}
                    productCategories={productCategories}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #e9ecef' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                style={{ borderRadius: '8px', fontWeight: '600', padding: '0.5rem 1.5rem' }}
              >
                <i className="fas fa-times me-2"></i>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Only show one backdrop at a time */}
      {!showAdvancedFilters && open && <div className="modal-backdrop fade show"></div>}
      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 10000 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <h5 className="modal-title">
                    <i className="fas fa-sliders-h me-2"></i>
                    Filtres avancés
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowAdvancedFilters(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-search me-1"></i>
                      Recherche produit
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Nom du produit..."
                      style={{ borderRadius: '8px' }}
                    />
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-money-bill-wave me-1"></i>
                        Prix min (MAD)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={priceFilter.min}
                        onChange={(e) => setPriceFilter({ ...priceFilter, min: e.target.value })}
                        placeholder="0"
                        style={{ borderRadius: '8px' }}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-money-bill-wave me-1"></i>
                        Prix max (MAD)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={priceFilter.max}
                        onChange={(e) => setPriceFilter({ ...priceFilter, max: e.target.value })}
                        placeholder="1000"
                        style={{ borderRadius: '8px' }}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-percentage me-1"></i>
                      Réduction minimum (%)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={discountFilter}
                      onChange={(e) => setDiscountFilter(e.target.value)}
                      placeholder="0"
                      style={{ borderRadius: '8px' }}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setProductSearch('');
                      setPriceFilter({ min: '', max: '' });
                      setDiscountFilter('');
                      setCurrentPage(1);
                    }}
                    style={{ borderRadius: '8px', fontWeight: '600' }}
                  >
                    <i className="fas fa-times me-2"></i>
                    Réinitialiser
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowAdvancedFilters(false);
                      setCurrentPage(1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                  >
                    <i className="fas fa-check me-2"></i>
                    Appliquer
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 9999 }}></div>
        </>
      )}    </>
  );
};

export default PromotionsModal;
